import React, { useState, useEffect } from "react";
import { CheckCircle2, Calendar, MessageSquare, ArrowRight, BookOpen, Clock, Download, ChevronRight, Volume2, Home } from "lucide-react";
import { CONFIG } from "../config";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

export default function ThankYouPage() {
  const [userName, setUserName] = useState("Expert");

  useEffect(() => {
    // Read registered user info from localStorage to personalize the page
    const userStr = localStorage.getItem("tm_webinar_registered_user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.name) {
          // Extract first name
          setUserName(user.name.split(" ")[0]);
        }
      } catch (err) {
        console.warn("Error parsing registered user in localStorage:", err);
      }
    }
  }, []);

  // Simple Confetti Animation Trigger on mount
  const [confettiActive, setConfettiActive] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setConfettiActive(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Helper to trigger Google Calendar Event Link
  const getGoogleCalendarUrl = () => {
    const title = encodeURIComponent(`Free Masterclass: ${CONFIG.webinarTitle}`);
    const details = encodeURIComponent(`Masterclass Host: ${CONFIG.creatorName}\nLink to attend: https://${CONFIG.domain}/live`);
    const dates = "20260221T160000Z/20260221T173000Z"; // Saturday, Feb 21, 2026 at 4:00 PM GMT
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&sf=true&output=xml`;
  };

  // Helper to trigger Apple iCal / Outlook ICS file download
  const handleDownloadICS = () => {
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//TagMango//MangoAI Funnel//EN
BEGIN:VEVENT
UID:uid1@tagmango.com
DTSTAMP:20260218T120000Z
ORGANIZER;CN="${CONFIG.creatorName}":MAILTO:support@${CONFIG.domain}
DTSTART:20260221T160000Z
DTEND:20260221T173000Z
SUMMARY:Free Masterclass: ${CONFIG.webinarTitle}
DESCRIPTION:Host: ${CONFIG.creatorName}\\nJoin Room: https://${CONFIG.domain}/live
LOCATION:https://${CONFIG.domain}/live
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", "ai_clarity_coach_webinar.ics");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-slate-800 pb-20">
      <SEO 
        title="Seat Reserved Successfully! | Thank You"
        description="Your seat for the AI Clarity Coach Launchpad webinar has been secured. Follow the steps on this page to prepare for the live session."
      />

      {/* Top minimalistic header */}
      <header className="py-6 px-4 bg-white border-b border-slate-100 shadow-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-extrabold text-slate-900 tracking-wide hover:text-teal-600 transition-colors flex items-center gap-2">
            <Home className="w-5 h-5 text-teal-600" />
            <span className="hidden sm:inline uppercase text-sm font-black tracking-widest">{CONFIG.creatorName}</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Webinar Room Active</span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-4 pt-10 sm:pt-16 relative">
        
        {/* Simple confetti mockup */}
        {confettiActive && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-10 flex flex-wrap justify-around opacity-60">
            {[...Array(20)].map((_, i) => {
              const colors = ["bg-teal-500", "bg-sky-500", "bg-yellow-400", "bg-rose-500", "bg-emerald-500"];
              const randomColor = colors[i % colors.length];
              const randomLeft = Math.floor(Math.random() * 100);
              const randomDelay = Math.random() * 2;
              return (
                <div 
                  key={i} 
                  className={`w-2.5 h-2.5 ${randomColor} rounded-full absolute animate-fall`} 
                  style={{ left: `${randomLeft}%`, top: "-10px", animationDelay: `${randomDelay}s` }}
                ></div>
              );
            })}
          </div>
        )}

        {/* Head Confirmation Message */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 mb-6 border border-emerald-100 shadow-inner">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 tracking-tight leading-tight">
            YOU'RE IN, {userName.toUpperCase()}!
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 mt-3 max-w-xl mx-auto font-normal">
            Your free VIP ticket to the AI Masterclass is secured. Let's get you prepared.
          </p>
        </div>

        {/* Details Card */}
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-10 shadow-sm max-w-3xl mx-auto mb-10 overflow-hidden relative">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-500 to-sky-500"></div>
          
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-widest font-extrabold text-slate-400">YOUR VIP ACCESS INFORMATION</span>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-1">{CONFIG.webinarTitle}</h2>
            <p className="text-sm font-semibold text-teal-600 mt-0.5">{CONFIG.webinarSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-b border-slate-100 py-6 mb-8 text-center sm:text-left">
            <div className="sm:pl-6 border-b sm:border-b-0 sm:border-r border-slate-100 pb-4 sm:pb-0">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Webinar Date</p>
              <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1">{CONFIG.defaultWebinarDate}</p>
            </div>
            <div className="sm:pl-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Webinar Time</p>
              <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1">{CONFIG.defaultWebinarTime}</p>
            </div>
          </div>

          <div className="space-y-4 max-w-md mx-auto">
            {/* Add to Google Calendar */}
            <a 
              href={getGoogleCalendarUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-5 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm sm:text-base active:scale-[0.99]"
              data-testid="add-to-calendar-google-button"
            >
              <Calendar className="w-4 h-4 text-red-500" />
              ADD TO GOOGLE CALENDAR
            </a>

            {/* Add to iCal / Outlook */}
            <button 
              onClick={handleDownloadICS}
              className="w-full py-3 px-5 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm sm:text-base active:scale-[0.99]"
              data-testid="add-to-calendar-ical-button"
            >
              <Download className="w-4 h-4 text-sky-500" />
              DOWNLOAD .ICS CALENDAR FILE
            </button>
          </div>

        </div>

        {/* VIP Action Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          
          {/* Action 1: WhatsApp Community */}
          <div className="bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#25D366]"></div>
            <div>
              <span className="text-xs font-bold uppercase text-[#25D366] bg-[#25D366]/5 px-2.5 py-1 rounded border border-[#25D366]/10 inline-block mb-4">
                STEP 1: VIP COMMUNITY
              </span>
              <h3 className="text-lg font-black text-slate-950">Join My Private Community</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Connect with {CONFIG.creatorName} and fellow knowledge-driven experts directly inside our WhatsApp community. We post announcements, workbook links, and early access codes here.
              </p>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-400 font-semibold">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span>1,420+ members active</span>
              </div>
            </div>
            
            <a 
              href={CONFIG.whatsappCommunityLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full mt-6 py-3.5 bg-[#25D366] hover:bg-[#20ba5a] text-white font-extrabold rounded-xl transition-all shadow-md active:scale-[0.98] flex items-center justify-center gap-2 text-sm"
              data-testid="whatsapp-community-button"
            >
              <MessageSquare className="w-4 h-4 fill-white" />
              JOIN THE COMMUNITY
            </a>
          </div>

          {/* Action 2: Webinar Entrance Room */}
          <div className="bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500"></div>
            <div>
              <span className="text-xs font-bold uppercase text-teal-600 bg-teal-50 px-2.5 py-1 rounded border border-teal-100 inline-block mb-4">
                STEP 2: ENTER ROOM
              </span>
              <h3 className="text-lg font-black text-slate-950">Test Connection & Attend</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                The webinar access room is ready! Click the button below to test your audio, verify your browser compatibility, and access the live streaming room directly.
              </p>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-400 font-semibold">
                <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
                <span>HD 1080p stream live feed active</span>
              </div>
            </div>

            <Link 
              to="/live"
              className="w-full mt-6 py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-extrabold rounded-xl transition-all shadow-md active:scale-[0.98] flex items-center justify-center gap-2 text-sm"
            >
              <BookOpen className="w-4 h-4" />
              ENTER WEBINAR ROOM
            </Link>
          </div>

        </div>

        {/* Optional Next Step Resource: AI Clarity Launchpad */}
        <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 max-w-3xl mx-auto mt-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(2,132,199,0.15),transparent)]"></div>
          <span className="text-xs font-bold uppercase tracking-wider text-sky-400 relative z-10">READY TO EXPLORE MORE?</span>
          <h3 className="text-lg sm:text-xl font-black mt-1 mb-3 relative z-10">
            THE AI CLARITY LAUNCHPAD
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 max-w-xl mx-auto leading-relaxed mb-6 relative z-10">
            Can't wait for the webinar and want to dive straight into turning your knowledge into structured digital assets? Explore our premium launchpad program today as an optional next step.
          </p>
          <a 
            href={CONFIG.launchpadLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-sky-500 hover:bg-sky-500/10 text-sky-400 hover:text-sky-300 font-bold rounded-xl text-xs sm:text-sm transition-all relative z-10 active:scale-[0.99]"
          >
            EXPLORE THE LAUNCHPAD
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>

      </main>
    </div>
  );
}
